import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { FiUser, FiEye, FiEyeOff, FiArrowLeft, FiLock } from "react-icons/fi";

import { ReactComponent as MrcMark } from "../../../assets/icons/logoMrc.svg";
import {
  selectLoginPending,
  selectLoggedIn,
  selectUser,
} from "../../../store/slices/auth/slice";
import { login } from "../../../store/slices/auth/asyncThunks";

import {
  Page,
  BackLink,
  Card,
  BrandRow,
  Title,
  Subtitle,
  ErrorMessage,
  Field,
  FieldLabel,
  InputWrap,
  Input,
  InputIcon,
  EyeButton,
  RememberRow,
  CheckboxLabel,
  SubmitButton,
  SecurityNote,
  Footer,
} from "./LoginPage.styles";

const REMEMBER_ME_KEY = "mrc_remembered_credentials";

// Početna stranica panela po roli, koristi se posle logina i za
// preusmeravanje već ulogovanih koji svrate na /login
const homeForRole = (role) => {
  switch (role) {
    case "admin":
      return "/admin-panel/kontrolna-tabla";
    case "band":
      return "/bend-panel/kontrolna-tabla";
    case "dj":
      return "/admin-panel/radio";
    default:
      return null;
  }
};

const readSavedCredentials = () => {
  try {
    const raw = localStorage.getItem(REMEMBER_ME_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector(selectLoginPending);
  const loggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // Učitaj sačuvano korisničko ime pri inicijalizaciji.
  // SIGURNOST (FE-01): lozinka se NIKAD ne čuva niti učitava iz localStorage.
  useEffect(() => {
    const saved = readSavedCredentials();
    if (saved?.rememberMe) {
      setUsername(saved.username || "");
      setRememberMe(true);
    }
    // Jednokratno čišćenje: ukloni lozinku iz starih zapisa (pre FE-01 popravke).
    if (saved && "password" in saved) {
      if (saved.rememberMe) {
        localStorage.setItem(
          REMEMBER_ME_KEY,
          JSON.stringify({ username: saved.username || "", rememberMe: true }),
        );
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
      }
    }
  }, []);

  const saveCredentials = () => {
    if (rememberMe) {
      // SIGURNOST (FE-01): čuvamo SAMO korisničko ime, nikad lozinku.
      localStorage.setItem(
        REMEMBER_ME_KEY,
        JSON.stringify({ username, rememberMe: true }),
      );
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
    }
  };

  const clearCredentials = () => {
    localStorage.removeItem(REMEMBER_ME_KEY);
  };

  const handleLogin = () => {
    setLoginError(null);

    dispatch(login({ username, password, rememberMe }))
      .unwrap()
      .then((response) => {
        // Sačuvaj ili obriši kredencijale na osnovu Remember Me
        saveCredentials();

        const target = homeForRole(response.adminUser?.role);
        if (target) {
          navigate(target, { state: { fromLogin: true } });
        }
      })
      .catch(() => {
        setLoginError("Pogrešno korisničko ime ili lozinka");
        // Obriši kredencijale ako login nije uspešan
        if (!rememberMe) {
          clearCredentials();
        }
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  // Već ulogovan korisnik nema šta da traži na loginu, pravo na svoj panel
  const roleHome = loggedIn && user?.role ? homeForRole(user.role) : null;
  if (roleHome) {
    return <Navigate to={roleHome} replace />;
  }

  return (
    <Page>
      <BackLink href="/">
        <FiArrowLeft size={15} /> Nazad na sajt
      </BackLink>

      <Card>
        <BrandRow>
          <MrcMark aria-hidden="true" />
          <span>МРЧ</span>
        </BrandRow>

        {/* <Eyebrow>Admin i bend panel</Eyebrow> */}
        <Title>Prijava</Title>
        <Subtitle>Prijavite se svojim nalogom za pristup panelu.</Subtitle>

        {loginError && <ErrorMessage>{loginError}</ErrorMessage>}

        <form onSubmit={handleSubmit}>
          <Field>
            <FieldLabel htmlFor="login-username">Korisničko ime</FieldLabel>
            <InputWrap>
              <Input
                id="login-username"
                name="username"
                autoComplete="username"
                placeholder="Unesite korisničko ime"
                value={username}
                $error={Boolean(loginError)}
                onChange={(e) => setUsername(e.target.value)}
              />
              <InputIcon>
                <FiUser size={16} />
              </InputIcon>
            </InputWrap>
          </Field>

          <Field>
            <FieldLabel htmlFor="login-password">Lozinka</FieldLabel>
            <InputWrap>
              <Input
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Unesite lozinku"
                value={password}
                $error={Boolean(loginError)}
                onChange={(e) => setPassword(e.target.value)}
              />
              <EyeButton
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Sakrij lozinku" : "Prikaži lozinku"}
              >
                {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </EyeButton>
            </InputWrap>
          </Field>

          <RememberRow>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Zapamti me
            </CheckboxLabel>
          </RememberRow>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (
              <>
                Prijavljivanje...
                <l-line-spinner
                  size="16"
                  stroke="2"
                  speed="1"
                  color="#ffffff"
                />
              </>
            ) : (
              "Prijavite se"
            )}
          </SubmitButton>
        </form>

        <SecurityNote>
          <FiLock size={13} /> Podaci su zaštićeni SSL enkripcijom
        </SecurityNote>

        <Footer>© {new Date().getFullYear()} МРЧ. Sva prava zadržana.</Footer>
      </Card>
    </Page>
  );
};

export default LoginPage;
